import { NavBarComponent } from './common/components/nav-bar/nav-bar.component';
import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './features/auth/data/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { SessionHubService } from './features/home/data/services/session-hub.service';
import { select, Store } from '@ngrx/store';
import { AdminState } from './features/admin/state/admin.reducer';
import { combineLatest, map, Observable } from 'rxjs';
import { HomeState } from './features/home/state/home.reducer';
import { filter, startWith } from 'rxjs/operators';
import {
  selectDashboardLoading,
  selectDashboardReady,
} from './features/admin/state/admin.selector';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, NgxSpinnerModule, NavBarComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  protected translate = inject(TranslateService);
  protected spinner = inject(NgxSpinnerService);
  protected authService = inject(AuthService);
  protected store = inject(Store);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private readonly sessionHubService = inject(SessionHubService);

  constructor() {
    const savedLang = localStorage.getItem('selectedLang');
    const defaultLang = 'en';
    const langToUse = savedLang || defaultLang;
    this.translate.addLangs(['en', 'pt', 'es', 'it']);
    this.translate.setDefaultLang(defaultLang);
    this.translate.use(langToUse);
  }

  ngOnInit(): void {
    const error = localStorage.getItem('logoutError');
    if (error) {
      this.toastr.error(error, 'Erro ao fazer login');
      localStorage.removeItem('logoutError');
    }

    const postLogoutToast = localStorage.getItem('postLogoutToast');
    if (postLogoutToast === 'sessionExpired') {
      localStorage.removeItem('postLogoutToast');
      this.translate
        .get(['toastr.sessionExpired', 'toastr.unauthorized'])
        .subscribe((t) => {
          this.toastr.error(
            t['toastr.sessionExpired'],
            t['toastr.unauthorized'],
          );
        });
    }

    this.sessionHubService.listenForForceLogout();
    this.claimSingleActiveTab();
    this.authService.generateAndStoreFingerprint().then();

    const routerLoading$: Observable<boolean> = this.router.events.pipe(
      map((event) => {
        if (event instanceof NavigationStart) return true;
        if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        )
          return false;
        return null;
      }),
      filter((value): value is boolean => value !== null),
      startWith(false),
    );

    const adminLoading$ = this.store.pipe(
      select('admin'),
      map((state: AdminState) => state.loading),
    );

    const homeLoading$ = this.store.pipe(
      select('home'),
      map((state: HomeState) => state.loading),
    );

    const dashboardReady$ = this.store.select(selectDashboardReady);
    const dashboardLoading$ = this.store.select(selectDashboardLoading);

    const isOnDashboard$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url.startsWith('/admin/dashboard')),
      startWith(this.router.url.startsWith('/admin/dashboard')),
    );

    combineLatest([
      routerLoading$,
      adminLoading$,
      homeLoading$,
      isOnDashboard$,
      dashboardLoading$,
      dashboardReady$,
    ]).subscribe(
      ([
        routerLoading,
        adminLoading,
        homeLoading,
        isOnDashboard,
        dashboardLoading,
        dashboardReady,
      ]) => {
        const shouldShowDashboardSpinner =
          isOnDashboard && (dashboardLoading || !dashboardReady);

        if (
          routerLoading ||
          adminLoading ||
          homeLoading ||
          shouldShowDashboardSpinner
        ) {
          this.spinner.show('main');
        } else {
          this.spinner.hide('main');
        }
      },
    );
  }

  private claimSingleActiveTab(): void {
    if (window.location.pathname.startsWith('/login')) return;

    const persistedAuth = localStorage.getItem('auth');
    if (!persistedAuth) return;
    try {
      const parsed = JSON.parse(persistedAuth);
      if (!parsed?.token) return;
      localStorage.setItem('forceLogoutFromOtherTabs', crypto.randomUUID());
    } catch {
      // ignore parse errors
    }
  }
}
